import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const NAV_LINKS = [
  { label: "Products", to: "/products", testid: "nav-link-products" },
  { label: "About", to: "/#about", testid: "nav-link-about" },
  { label: "Industries", to: "/#industries", testid: "nav-link-industries" },
  { label: "Quality", to: "/#quality", testid: "nav-link-quality" },
  { label: "Journal", to: "/blog", testid: "nav-link-blog" },
  { label: "Contact", to: "/contact", testid: "nav-link-contact" },
];

const Header = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed top-0 inset-x-0 z-50 bg-bone/80 backdrop-blur-xl border-b border-line"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 lg:h-20 flex items-center justify-between">
          <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 group">
            <img src="/logo.png" alt="SNP Plastech logo" className="h-9 lg:h-11 w-auto transition-transform duration-300 group-hover:scale-105" />
            <span className="font-display font-semibold text-lg lg:text-xl tracking-tight">
              SNP<span className="font-light text-obsidian/60"> PLASTECH</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                data-testid={link.testid}
                className="text-sm font-medium text-obsidian/70 hover:text-obsidian transition-colors duration-200 tracking-wide"
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="header-enquiry-cta"
              className="group inline-flex items-center gap-2 bg-obsidian text-bone text-sm font-medium px-5 py-2.5 rounded-full hover:bg-emerald transition-colors duration-300"
            >
              Start an Enquiry
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(true)}
            className="lg:hidden p-2 -mr-2"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" strokeWidth={1.5} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-obsidian text-bone flex flex-col"
          >
            <div className="flex items-center justify-between px-6 h-16 border-b border-bone/10">
              <span className="font-display font-semibold text-lg tracking-tight">SNP PLASTECH</span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="p-2 -mr-2"
                aria-label="Close menu"
              >
                <X className="w-6 h-6" strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-6 gap-2">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.label}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.06, duration: 0.5 }}
                >
                  <Link
                    to={link.to}
                    data-testid={`mobile-${link.testid}`}
                    onClick={() => setOpen(false)}
                    className="font-display font-medium text-4xl sm:text-5xl tracking-tight py-2 block hover:text-emerald transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="px-6 pb-10 text-sm text-bone/50 tracking-wide">
              Plastic packaging, engineered with precision.
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
